import type { BookContextType } from "../inspection-book/book-context-resolver.js";

/**
 * DOCUMENTAÇÃO E BIBLIOTECA TÉCNICA (Bloco 03).
 *
 * Estrutura oficial para anexar/vincular/versionar/consultar documentos
 * técnicos — substitui a antiga página stub (fetch simulado, permissão
 * hardcoded). Reaproveita o mesmo TIPO DE CONTEXTO do Book de Inspeção
 * (`book-context-resolver.ts`: SISTEMA_TUBULACAO / TUBULACAO_LINHA /
 * EQUIPAMENTO_ESTATICO) para não inventar um segundo catálogo de contexto.
 *
 * Catálogo de TIPO DE DOCUMENTO extensível — nunca hardcoded na UI: a tela
 * só lê `TECHNICAL_DOCUMENT_TYPES`.
 */
export type TechnicalDocumentType =
  | "ISOMETRIC" // Isométrico de Inspeção
  | "PIT" // Plano de Inspeção e Teste
  | "FLOWCHART" // Fluxograma / P&ID
  | "PIPING_PLAN" // Planta de Tubulação
  | "DATASHEET" // Folha de Dados
  | "DRAWING" // Desenho Técnico
  | "RISK_MATRIX" // Matriz de Risco (CUI/CSI)
  | "NDT_COMPLEMENTARY" // Relatório de END Complementar
  | "LINE_LIST" // Lista de Linhas (já usada pelo Book — mantém o mesmo código)
  | "OTHER";

export const TECHNICAL_DOCUMENT_TYPES: ReadonlyArray<{ code: TechnicalDocumentType; label: string }> = [
  { code: "ISOMETRIC", label: "Isométrico de Inspeção" },
  { code: "PIT", label: "PIT" },
  { code: "FLOWCHART", label: "Fluxograma" },
  { code: "PIPING_PLAN", label: "Planta de Tubulação" },
  { code: "DATASHEET", label: "Folha de Dados" },
  { code: "DRAWING", label: "Desenho Técnico" },
  { code: "RISK_MATRIX", label: "Matriz de Risco (CUI/CSI)" },
  { code: "NDT_COMPLEMENTARY", label: "Relatório de END Complementar" },
  { code: "LINE_LIST", label: "Lista de Linhas" },
  { code: "OTHER", label: "Outro" }
];

export const TECHNICAL_DOCUMENT_TYPE_LABELS: Record<TechnicalDocumentType, string> = Object.fromEntries(
  TECHNICAL_DOCUMENT_TYPES.map(t => [t.code, t.label])
) as Record<TechnicalDocumentType, string>;

export type TechnicalDocumentStatus = "ACTIVE" | "INACTIVE";

/**
 * O documento-conceito (o "TAG" do documento): agrupa suas revisões. A
 * revisão vigente é sempre `revisions[revisions.length - 1]` — nunca
 * sobrescrita; uma nova revisão é um novo `TechnicalDocumentRevision`.
 */
export interface TechnicalDocument {
  id: string;
  clientId: string;
  projectId: string;
  unitAreaId: string | null;
  contextType: BookContextType;
  /** O Equipment do vínculo real — nunca um TAG digitado livre. */
  equipmentId: string;
  equipmentTag: string;
  documentType: TechnicalDocumentType;
  title: string;
  description: string | null;
  status: TechnicalDocumentStatus;
  inactivatedAt?: string | null;
  inactivatedBy?: string | null;
  inactivationReason?: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

/**
 * Uma revisão imutável do documento: o binário e os metadados de quem/quando
 * anexou. `storageRef` é uma referência opaca (nunca um caminho físico) que
 * só o `DocumentStorageService` sabe resolver.
 */
export interface TechnicalDocumentRevision {
  id: string;
  documentId: string;
  revision: string;
  storageRef: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  hash: string;
  documentDate: string | null;
  uploadedAt: string;
  uploadedBy: string;
}

export interface TechnicalDocumentWithRevisions {
  document: TechnicalDocument;
  revisions: readonly TechnicalDocumentRevision[];
}

export function currentRevisionOf(entry: TechnicalDocumentWithRevisions): TechnicalDocumentRevision | null {
  return entry.revisions.length > 0 ? entry.revisions[entry.revisions.length - 1]! : null;
}

export interface TechnicalDocumentFilter {
  clientId?: string;
  projectId?: string;
  unitAreaId?: string;
  equipmentId?: string;
  /** Busca por TAG do Equipment vinculado — nunca `filename.includes(tag)`. */
  equipmentTag?: string;
  documentType?: TechnicalDocumentType;
  status?: TechnicalDocumentStatus;
  from?: string;
  to?: string;
}
